import React from 'react';
import { AnimationConfig, AnimationTimingMode, ANIMATION_TIMING_DEFAULTS } from '../types/animated';
import { AnimationHelpers } from './AnimationHelpers';

/**
 * Animation state enum for unified timing system
 */
export enum AnimationState {
    ENTRANCE = 'ENTRANCE',
    VISIBLE = 'VISIBLE', 
    EXIT = 'EXIT',
    INACTIVE = 'INACTIVE'
}

/**
 * Enhanced result from getSequenceAnimationStyles with state information
 */
export interface SequenceAnimationResult {
    animationStyles: React.CSSProperties;
    visibility: React.CSSProperties;
    animationState: AnimationState;
    progress?: {
        entrance?: number;
        exit?: number;
    };
}

/**
 * Centralized helper for handling animation timing within Remotion sequences.
 * This ensures consistent behavior across all component types (overlay, text-overlay, caption, branding).
 * 
 * ENHANCED: Now serves as single source of truth for both animation execution AND state information.
 * 
 * Key principle: When inside a Remotion Sequence, currentFrame is relative to the sequence start,
 * so we need special handling for components with and without animations.
 */
export class SequenceTimingHelper {
    /**
     * Enhanced animation timing calculation with state information.
     * Calculates animation window based on timing modes and returns current state.
     */
    private static calculateAnimationWindow(
        boundaryTime: number,
        animationDuration: number,
        timingMode: AnimationTimingMode
    ): { animationStart: number; animationEnd: number } {
        let animationStart = boundaryTime;
        let animationEnd = boundaryTime;
        
        switch (timingMode) {
            case 'before-at':
                // Animation completes AT boundary time
                animationStart = boundaryTime - animationDuration;
                animationEnd = boundaryTime;
                break;
            case 'at-after':
                // Animation starts AT boundary time
                animationStart = boundaryTime;
                animationEnd = boundaryTime + animationDuration;
                break;
            case 'centered':
                // Animation centered on boundary time
                animationStart = boundaryTime - (animationDuration / 2);
                animationEnd = boundaryTime + (animationDuration / 2);
                break;
        }
        
        return { animationStart, animationEnd };
    }

    /**
     * Gets animation styles for components rendered inside a Remotion Sequence.
     * Handles both animated and non-animated components consistently.
     * 
     * ENHANCED: Now supports timing modes and returns animation state information
     * for unified sequence naming and animation execution.
     * 
     * @param currentFrame - The current frame relative to the sequence start
     * @param fps - Frames per second
     * @param duration - Duration of the item in seconds
     * @param entranceAnimation - Optional entrance animation config
     * @param exitAnimation - Optional exit animation config
     * @param baseTransforms - Optional base transforms
     * @param defaultOpacity - Default opacity when no animations are present
     * @returns Animation styles, visibility, and current animation state
     */
    static getSequenceAnimationStyles(
        currentFrame: number,
        fps: number,
        duration: number | undefined,
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig,
        baseTransforms?: { translateX?: string; translateY?: string; scale?: number; rotate?: string },
        defaultOpacity: number = 1
    ): SequenceAnimationResult {
        const hasAnimations = entranceAnimation || exitAnimation;
        const currentTime = currentFrame / fps;
        
        // CRITICAL FIX: Check if we're in a valid frame range
        if (currentFrame < 0) {
            // Negative frame detected, hiding content
            // Convert baseTransforms to proper CSS transform string
            const transformString = AnimationHelpers.combineTransforms(baseTransforms ?? {}, {});
            const cssStyles = transformString !== 'none' ? { transform: transformString } : {};
            
            return {
                animationStyles: cssStyles,
                visibility: { opacity: 0 },
                animationState: AnimationState.INACTIVE
            };
        }
        
        if (hasAnimations) {
            // ENHANCED: Calculate timing windows with timing mode support
            const entranceDuration = entranceAnimation?.timing.duration ?? 0;
            const exitDuration = exitAnimation?.timing.duration ?? 0;
            const entranceMode: AnimationTimingMode = entranceAnimation?.timingMode ?? ANIMATION_TIMING_DEFAULTS.ENTRANCE;
            const exitMode: AnimationTimingMode = exitAnimation?.timingMode ?? ANIMATION_TIMING_DEFAULTS.EXIT;
            const contentDuration = duration || 0;
            
            // For sequence-relative timing, item is scheduled to be visible from entrance end to exit start
            // Calculate entrance and exit windows in sequence-relative time
            let entranceStart = 0;
            let entranceEnd = 0;
            let exitStart = contentDuration;
            let exitEnd = contentDuration;
            
            if (entranceAnimation && entranceDuration > 0) {
                // In sequence-relative timing, item should be visible at entranceDuration
                const entranceWindow = this.calculateAnimationWindow(
                    entranceDuration, // Boundary is when item should be visible
                    entranceDuration,
                    entranceMode
                );
                entranceStart = Math.max(0, entranceWindow.animationStart);
                entranceEnd = entranceWindow.animationEnd;
            }
            
            if (exitAnimation && exitDuration > 0) {
                // Exit relative to when content should end (after entrance)
                const contentEndTime = entranceDuration + contentDuration;
                const exitWindow = this.calculateAnimationWindow(
                    contentEndTime,
                    exitDuration,
                    exitMode
                );
                exitStart = exitWindow.animationStart;
                exitEnd = exitWindow.animationEnd;
            }
            
            // Determine current animation state
            let animationState = AnimationState.VISIBLE;
            let entranceProgress = 1;
            let exitProgress = 0;
            
            // Check entrance animation
            if (entranceDuration > 0 && currentTime >= entranceStart && currentTime < entranceEnd) {
                animationState = AnimationState.ENTRANCE;
                entranceProgress = (currentTime - entranceStart) / entranceDuration;
            } else if (entranceDuration > 0 && currentTime < entranceStart) {
                animationState = AnimationState.INACTIVE;
                entranceProgress = 0;
            }
            
            // Check exit animation (overrides entrance state if both active)
            if (exitDuration > 0 && currentTime >= exitStart && currentTime <= exitEnd) {
                animationState = AnimationState.EXIT;
                exitProgress = (currentTime - exitStart) / exitDuration;
            } else if (exitDuration > 0 && currentTime > exitEnd) {
                animationState = AnimationState.INACTIVE;
                exitProgress = 1;
            }
            
            // Use AnimationHelpers with enhanced timing mode support
            const { animationStyles, visibility } = AnimationHelpers.getAnimationStylesWithTimingModes(
                currentFrame,
                fps,
                0, // startAt is 0 in sequence-relative timing
                duration,
                entranceAnimation,
                exitAnimation,
                baseTransforms,
                entranceProgress,
                exitProgress
            );
            
            return {
                animationStyles,
                visibility,
                animationState,
                progress: {
                    entrance: entranceProgress,
                    exit: exitProgress
                }
            };
        } else {
            // For items without animations, they should be fully visible during their sequence duration
            // Convert baseTransforms to proper CSS transform string
            const transformString = AnimationHelpers.combineTransforms(baseTransforms ?? {}, {});
            const cssStyles = transformString !== 'none' ? { transform: transformString } : {};
            
            return {
                animationStyles: cssStyles,
                visibility: { opacity: defaultOpacity },
                animationState: AnimationState.VISIBLE
            };
        }
    }

    /**
     * Determines if an item should use basic or advanced rendering based on animations.
     * This helps maintain consistency across component types.
     */
    static shouldUseAdvancedRendering(
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig,
        customAnimations?: AnimationConfig[]
    ): boolean {
        return !!(entranceAnimation || exitAnimation || (customAnimations && customAnimations.length > 0));
    }

    /**
     * Creates a consistent sequence name format across all component types.
     * Includes animation information when present.
     */
    static createSequenceName(
        componentType: string,
        id: string,
        startAt: number,
        duration: number | undefined,
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig,
        isBasic: boolean = false
    ): string {
        const endTime = duration ? startAt + duration : '∞';
        const timeInfo = `${startAt}s→${endTime}${typeof endTime === 'number' ? 's' : ''}`;
        
        if (isBasic) {
            return `${componentType}-${id}-${timeInfo}[basic]`;
        }
        
        const animationInfo = [];
        if (entranceAnimation) {
            animationInfo.push(`enter:${entranceAnimation.type}(${entranceAnimation.timing.duration}s)`);
        }
        if (exitAnimation) {
            animationInfo.push(`exit:${exitAnimation.type}(${exitAnimation.timing.duration}s)`);
        }
        const animationLabel = animationInfo.length > 0 ? `[${animationInfo.join('|')}]` : '';
        
        return `${componentType}-${id}-${timeInfo}${animationLabel}`;
    }

    /**
     * Creates a dynamic sequence name with current animation state.
     * Used by DynamicOverlaySequence for real-time state updates.
     */
    static createDynamicSequenceName(
        componentType: string,
        id: string,
        startAt: number,
        duration: number | undefined,
        animationState: AnimationState,
        progress?: { entrance?: number; exit?: number }
    ): string {
        const endTime = duration ? startAt + duration : '∞';
        const timeInfo = `${startAt}s→${endTime}${typeof endTime === 'number' ? 's' : ''}`;
        const baseName = `${componentType}-${id}-${timeInfo}`;
        
        switch (animationState) {
            case AnimationState.ENTRANCE:
                const entrancePercent = ((progress?.entrance ?? 0) * 100).toFixed(1);
                return `${baseName} [ENTRANCE ${entrancePercent}%]`;
            case AnimationState.EXIT:
                const exitPercent = ((progress?.exit ?? 0) * 100).toFixed(1);
                return `${baseName} [EXIT ${exitPercent}%]`;
            case AnimationState.VISIBLE:
                return `${baseName} [VISIBLE]`;
            case AnimationState.INACTIVE:
            default:
                return baseName; // No state suffix when inactive
        }
    }

    /**
     * Calculates sequence timing with proper animation handling.
     * This is a wrapper around AnimationHelpers.calculateSequenceTiming for consistency.
     */
    static calculateSequenceTiming(
        startAt: number,
        duration: number | undefined,
        fps: number,
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig
    ): { startFrame: number; durationFrames: number } {
        return AnimationHelpers.calculateSequenceTiming(
            startAt,
            duration,
            fps,
            entranceAnimation,
            exitAnimation
        );
    }

    /**
     * Calculate sound effect timing windows using the same centralized logic as animations.
     * This ensures perfect synchronization between sound effects and their associated animations.
     * 
     * @param startAt - Item's scheduled start time
     * @param duration - Item's duration
     * @param animationType - 'entrance' or 'exit'
     * @param animation - The animation config (entrance or exit)
     * @param soundDelay - Optional delay from the sound config
     * @returns Sound timing window with start/end times
     */
    static calculateSoundEffectWindow(
        startAt: number,
        duration: number | undefined,
        animationType: 'entrance' | 'exit',
        animation: AnimationConfig | undefined,
        soundDelay: number = 0
    ): { 
        windowStart: number; 
        windowEnd: number; 
        animationStart: number; 
        animationEnd: number;
        maxDuration: number;
        isValid: boolean;
    } {
        if (!animation) {
            // No animation - sound plays during item's scheduled time
            const windowStart = startAt + soundDelay;
            const windowEnd = startAt + (duration ?? 0);
            return {
                windowStart,
                windowEnd,
                animationStart: startAt,
                animationEnd: startAt + (duration ?? 0),
                maxDuration: Math.max(0, windowEnd - windowStart),
                isValid: windowStart < windowEnd
            };
        }

        const animationDuration = animation.timing.duration;
        const animationDelay = animation.timing.delay ?? 0;
        const timingMode: AnimationTimingMode = animation.timingMode ?? (
            animationType === 'entrance' ? ANIMATION_TIMING_DEFAULTS.ENTRANCE : ANIMATION_TIMING_DEFAULTS.EXIT
        );

        let animationWindow: { animationStart: number; animationEnd: number };

        if (animationType === 'entrance') {
            // Use the same timing logic as SequenceTimingHelper
            animationWindow = this.calculateAnimationWindow(
                startAt, // Entrance animation boundary is item start
                animationDuration,
                timingMode
            );
        } else {
            // Exit animation boundary is item end
            const itemEnd = startAt + (duration ?? 0);
            animationWindow = this.calculateAnimationWindow(
                itemEnd,
                animationDuration,
                timingMode
            );
        }

        // Sound can play during the entire animation window
        const soundStart = animationWindow.animationStart + soundDelay;
        const soundEnd = animationWindow.animationEnd;
        
        return {
            windowStart: soundStart,
            windowEnd: soundEnd,
            animationStart: animationWindow.animationStart,
            animationEnd: animationWindow.animationEnd,
            maxDuration: Math.max(0, soundEnd - soundStart),
            isValid: soundStart < soundEnd
        };
    }

    /**
     * Enhanced sound constraints calculation that uses centralized timing logic.
     * This replaces the old calculateSoundConstraints to ensure consistency with animations.
     */
    static calculateSoundConstraints(
        startAt: number,
        duration: number | undefined,
        animationType: 'entrance' | 'exit',
        animation: AnimationConfig | undefined,
        soundDelay: number = 0,
        originalSoundDuration: number = 0
    ) {
        const window = this.calculateSoundEffectWindow(
            startAt,
            duration,
            animationType,
            animation,
            soundDelay
        );

        return {
            windowStart: window.windowStart,
            windowEnd: window.windowEnd,
            maxDuration: window.maxDuration,
            effectiveStart: window.windowStart,
            effectiveEnd: window.windowEnd,
            willBeTrimmed: originalSoundDuration > 0 && originalSoundDuration > window.maxDuration,
            isValid: window.isValid
        };
    }
}